import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

const toneClasses = {
  success: 'text-emerald-600 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20',
  warning: 'text-amber-600 bg-amber-50 dark:text-amber-300 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20',
  info: 'text-blue-600 bg-blue-50 dark:text-blue-300 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/20',
  muted: 'text-slate-500 bg-slate-100 dark:text-slate-400 dark:bg-white/5 border-slate-200 dark:border-white/10',
};

/**
 * CreditStatusSummary - Affiche le résumé des crédits et du plan
 * 
 * @param {string} variant - 'default' | 'dashboard' | 'compact'
 *   - 'default': Layout horizontal standard
 *   - 'dashboard': Layout vertical avec plus de détails pour le dashboard
 *   - 'compact': Version minimale inline
 */
const CreditStatusSummary = ({
  credits = 0,
  planLabel = 'Gratuit',
  planType = 'free',
  subscriptionStatus = 'inactive',
  monthlyCredits,
  creditsUsedThisCycle,
  monthlyPrice,
  nextBilling,
  isUnlimited = false,
  onRecharge,
  onHistory,
  onManagePlan,
  onUpgrade,
  freeDownloadBadge,
  className = '',
  showActions = true,
  variant = 'default',
}) => {
  const { t } = useTranslation('common');
  const lowCredits = (credits ?? 0) <= 0;
  const isPro = planType === 'pro';
  const planChipClass = isPro
    ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200'
    : 'bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white';
  
  const statusLabel = t(`plan.${subscriptionStatus}`, { defaultValue: isPro ? t('plan.active') : t('plan.inactive') });
  
  // Variant: Dashboard - Layout vertical avec design card premium
  if (variant === 'dashboard') {
    return (
      <div className={`rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 shadow-lg overflow-hidden h-full flex flex-col ${className}`}>
        {/* Header avec gradient pour Pro */}
        <div className={`p-4 ${isPro ? 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-500/10 dark:to-orange-500/10' : 'bg-slate-50 dark:bg-white/5'}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`h-11 w-11 rounded-xl flex items-center justify-center text-lg shadow-sm ${isPro ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white' : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}>
                <FontAwesomeIcon icon={isPro ? "crown" : "user"} />
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-slate-900 dark:text-white text-base">{planLabel}</h3>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide ${planChipClass}`}>
                    {isPro ? 'MUZO+' : 'FREE'}
                  </span>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">{statusLabel}</span>
                </div>
              </div>
            </div>
            {onManagePlan && (
              <button onClick={onManagePlan} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white transition rounded-lg hover:bg-white/50 dark:hover:bg-white/5" title={t('subscription.manage')}>
                <FontAwesomeIcon icon="cog" />
              </button>
            )}
          </div>
        </div>
        
        {/* Credits Section */}
        <div className="flex-1 p-5 flex flex-col justify-between">
          <div className="space-y-4">
            {/* Solde principal */}
            <div className="text-center">
              <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider font-bold mb-1">{t('credits.balance')}</p>
              <div className="flex items-baseline justify-center gap-2">
                <span className={`text-4xl font-bold ${lowCredits ? 'text-rose-500' : 'text-slate-900 dark:text-white'}`}>
                  {credits ?? 0}
                </span>
                <span className="text-sm text-slate-400 font-medium">{t('credits.label')}</span>
              </div>
</div>

            {/* Free Download Badge */}
            {freeDownloadBadge && (
              <div className={`flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-xl text-xs font-medium border ${toneClasses[freeDownloadBadge.tone]}`}>
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={freeDownloadBadge.tone === 'success' ? 'check-circle' : freeDownloadBadge.tone === 'info' ? 'infinity' : 'clock'} />
                  <span>{freeDownloadBadge.text}</span>
                </div>
                {freeDownloadBadge.countdown && (
                  <span className="font-bold text-sm">{freeDownloadBadge.countdown}</span>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          {showActions && (
            <div className="pt-4 space-y-2">
              {onRecharge && (
                <button onClick={onRecharge} className="w-full rounded-xl bg-indigo-600 text-white px-4 py-2.5 text-sm font-bold hover:bg-indigo-500 transition shadow-sm shadow-indigo-500/20 flex items-center justify-center gap-2">
                  <FontAwesomeIcon icon="plus" />
                  Recharger mes crédits
                </button>
              )}
              {!isPro && onUpgrade && (
                <button onClick={onUpgrade} className="w-full rounded-xl border border-amber-300 dark:border-amber-500/30 text-amber-700 dark:text-amber-300 px-4 py-2 text-xs font-semibold hover:bg-amber-50 dark:hover:bg-amber-500/10 transition flex items-center justify-center gap-2">
                  <FontAwesomeIcon icon="crown" />
                  Passer à Muzo+
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Variant: Compact - Version inline minimale
  if (variant === 'compact') {
    return (
      <div className={`inline-flex items-center gap-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white/90 dark:bg-slate-900/70 px-3 py-2 ${className}`}>
        <div className={`h-7 w-7 rounded-lg flex items-center justify-center text-xs ${isPro ? 'bg-amber-100 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400' : 'bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-400'}`}>
          <FontAwesomeIcon icon={isPro ? "crown" : "coins"} />
        </div>
        <div className="flex items-baseline gap-1">
          <span className={`text-lg font-bold ${lowCredits ? 'text-rose-500' : 'text-slate-900 dark:text-white'}`}>
            {credits ?? 0}
          </span>
          <span className="text-xs text-slate-400">cr</span>
        </div>
        {onRecharge && (
          <button onClick={onRecharge} className="ml-1 rounded-lg bg-indigo-600 text-white px-2.5 py-1 text-[10px] font-bold hover:bg-indigo-500 transition">
            <FontAwesomeIcon icon="plus" />
          </button>
        )}
      </div>
    );
  }

  // Variant: Default - Layout horizontal standard
  return (
    <div className={`rounded-2xl border border-slate-200 dark:border-white/10 bg-white/90 dark:bg-slate-900/70 shadow-sm overflow-hidden ${className}`}>
      <div className="flex flex-col md:flex-row items-center gap-4 p-4">
        
        {/* Plan Info */}
        <div className="flex items-center gap-4 w-full md:w-auto min-w-max">
           <div className={`h-10 w-10 rounded-xl flex items-center justify-center text-lg ${isPro ? 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400' : 'bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-400'}`}>
              <FontAwesomeIcon icon={isPro ? "crown" : "user"} />
           </div>
           <div>
              <div className="flex items-center gap-2">
                 <h3 className="font-bold text-slate-900 dark:text-white text-base">{planLabel}</h3>
                 <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${planChipClass}`}>
                    {isPro ? 'MUZO+' : 'FREE'}
                 </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">{statusLabel}</p>
           </div>
        </div>

        {/* Divider */}
        <div className="hidden md:block w-px h-8 bg-slate-200 dark:bg-white/10 mx-2" />

        {/* Credits & Usage */}
        <div className="flex-1 w-full flex flex-col sm:flex-row items-center justify-between gap-4">
           <div className="flex items-center gap-6 w-full sm:w-auto">
             <div>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider font-bold mb-0.5">Crédits</p>
                <div className="flex items-baseline gap-1.5">
                   <span className={`text-xl font-bold ${lowCredits ? 'text-rose-500' : 'text-slate-900 dark:text-white'}`}>
                      {credits ?? 0}
                   </span>
                   {monthlyCredits && <span className="text-xs text-slate-400 font-medium">/ {monthlyCredits}</span>}
                </div>
             </div>

             {/* Free Download Status (Dynamic) */}
             {freeDownloadBadge && (
               <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border ${toneClasses[freeDownloadBadge.tone]}`}>
                  <FontAwesomeIcon icon={freeDownloadBadge.tone === 'success' ? 'check-circle' : 'clock'} />
                  {freeDownloadBadge.text}
               </div>
             )}
           </div>

           {/* Actions */}
           {showActions && (
             <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                {onRecharge && (
                   <button onClick={onRecharge} className="whitespace-nowrap rounded-xl bg-indigo-600 text-white px-4 py-2 text-xs font-bold hover:bg-indigo-500 transition shadow-sm shadow-indigo-500/20">
                      <FontAwesomeIcon icon="plus" className="mr-1.5" />
                      Recharger
                   </button>
                )}
                {onManagePlan && (
                   <button onClick={onManagePlan} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white transition" title="Gérer l'abonnement">
                      <FontAwesomeIcon icon="cog" />
                   </button>
                )}
             </div>
           )}
        </div>

      </div>
    </div>
  );
};

export default CreditStatusSummary;
